/**
 * Warns before a rep loses typed-in data by leaving too early (task 11 of
 * the 2026-09-06 batch). Two of the three ways that happens are covered:
 *
 *   - Closing the tab or reloading: the native `beforeunload` prompt.
 *   - The app's own back/cancel controls: call `confirmDiscard(isDirty)`
 *     before navigating, wherever a page's own button would discard data.
 *
 * Not covered: the browser's hardware back button or an edge-swipe-back
 * gesture (Capacitor's WKWebView can enable one on iOS). React Router
 * cannot block browser history navigation with the plain `<BrowserRouter>`
 * this app uses; doing that reliably needs a data router
 * (`createBrowserRouter`), which is a bigger change than this fix. Both
 * `beforeunload` and `confirmDiscard` use the browser's own native prompt
 * rather than a custom modal, since `beforeunload` can only ever show the
 * native one anyway (browsers ignore any custom text or markup there), and
 * using the same kind of prompt for both keeps the two consistent.
 */
import { useEffect } from 'react';

export function useUnsavedChangesGuard(isDirty) {
  useEffect(() => {
    const handler = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);
}

export function confirmDiscard(isDirty) {
  if (!isDirty) return true;
  return window.confirm('You have unsaved changes. Leave without saving?');
}
